import {
  Button,
  Checkbox,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Search } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Spinner } from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import {
  useGetOrganizationCollectionEndpoint,
  useSetOrganizationGroupMembers,
} from '../../../../queries';

interface AssignOrganizationsModalProps {
  isOpen: boolean;
  groupId: string | null;
  groupName?: string;
  initialAssigned: string[];
  onClose: () => void;
}

const normalize = (s: unknown): string =>
  (typeof s === 'string' ? s.toLowerCase() : '');

export const AssignOrganizationsModal: FC<AssignOrganizationsModalProps> = ({
  isOpen,
  groupId,
  groupName,
  initialAssigned,
  onClose,
}) => {
  const { showSuccess, showError } = useHeroToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(initialAssigned));

  const { data: organizations, isLoading } = useGetOrganizationCollectionEndpoint();
  const { mutateAsync: saveMembers } = useSetOrganizationGroupMembers();
  const [isSaving, setIsSaving] = useState(false);
  const isPending = isSaving;

  // Sync when modal re-opens with a different group.
  useEffect(() => {
    if (isOpen) setSelected(new Set(initialAssigned));
    else setSearch('');
  }, [isOpen, initialAssigned]);

  const filtered = useMemo(() => {
    const list = organizations ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((o) => {
      const haystacks = [
        normalize(o.name),
        normalize(o.hotel),
        normalize(o.companyName),
        normalize(o.companyEmail),
        normalize(o.city),
        normalize(o.code),
      ];
      return haystacks.some((h) => h.includes(needle));
    });
  }, [organizations, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0
    && filtered.every((o) => selected.has(o.id));

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((o) => next.delete(o.id));
      } else {
        filtered.forEach((o) => next.add(o.id));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!groupId) return;
    const initialSet = new Set(initialAssigned);
    const selectedArr = Array.from(selected);
    const toAdd = selectedArr.filter((id) => !initialSet.has(id));
    const toRemove = initialAssigned.filter((id) => !selected.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await saveMembers({ groupId, organizationIds: selectedArr });
      showSuccess('Zapisano przypisane organizacje');
      onClose();
    } catch (err: any) {
      showError(err?.message || 'Nie udało się zapisać');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open && !isPending) onClose(); }}
      size="3xl"
      scrollBehavior="inside"
      isDismissable={!isPending}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span>Przypisz organizacje</span>
              {groupName && (
                <span className="text-xs font-normal text-gray-500">
                  Grupa: <span className="font-medium text-gray-700">{groupName}</span>
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Input
                  size="sm"
                  placeholder="Szukaj po nazwie, hotelu, mieście, kodzie..."
                  value={search}
                  onValueChange={setSearch}
                  startContent={<Search size={14} className="text-gray-400" />}
                  isClearable
                  onClear={() => setSearch('')}
                  classNames={{ base: 'flex-1 min-w-[240px]' }}
                />
                <Chip size="sm" variant="flat" color="primary">
                  Wybrano: {selected.size}
                </Chip>
              </div>

              {isLoading ? (
                <div className="py-8 flex justify-center">
                  <Spinner showLabel={false} />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-3 py-2 w-10">
                          <Checkbox
                            isSelected={allFilteredSelected}
                            isIndeterminate={!allFilteredSelected && filtered.some((o) => selected.has(o.id))}
                            onValueChange={toggleAllFiltered}
                            aria-label="Zaznacz wszystkie"
                          />
                        </th>
                        <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Hotel</th>
                        <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Miasto</th>
                        <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Kod</th>
                        <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                            Brak organizacji
                          </td>
                        </tr>
                      )}
                      {filtered.map((org) => {
                        const isSel = selected.has(org.id);
                        return (
                          <tr
                            key={org.id}
                            className={`border-t border-gray-100 cursor-pointer transition-colors ${
                              isSel ? 'bg-primary/5' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => toggle(org.id)}
                          >
                            <td className="px-3 py-2">
                              <Checkbox
                                isSelected={isSel}
                                onValueChange={() => toggle(org.id)}
                                aria-label={`Zaznacz ${org.hotel || org.name}`}
                              />
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">
                              {org.hotel || org.name || '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{org.city || '—'}</td>
                            <td className="px-3 py-2">
                              {org.code ? (
                                <Chip size="sm" variant="flat">{org.code}</Chip>
                              ) : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-600 truncate max-w-[200px]">
                              {org.companyEmail || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isPending}>
                Anuluj
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isLoading={isPending}
                isDisabled={!groupId}
              >
                Zapisz przypisanie
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
